import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useMediaQuery,
  useTheme,
  Chip,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { format, formatDistanceToNow } from "date-fns";
import {
  db,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp,
  getDocs,
  deleteDoc,
  writeBatch,
} from "./firebaseConfig";

const ErrorLogsDialog = ({ open, onClose }) => {
  const [errorLogs, setErrorLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addingTestError, setAddingTestError] = useState(false);
  const [selectedError, setSelectedError] = useState(null);
  const [errorDetailOpen, setErrorDetailOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));


  useEffect(() => {
    if (!open) return;
    
    setLoading(true);
    
    const unsub = onSnapshot(collection(db, 'ErrorLogs'), (snapshot) => {
      const logsData = snapshot.docs
        .filter(doc => doc.id !== '-Placeholder')
        .map(doc => ({
          id: doc.id,
          ...doc.data(),
          errorRead: doc.data().errorRead || false
        }))
        .sort((a, b) => {
          const aTime = a.timestamp?.toDate?.() || new Date(0);
          const bTime = b.timestamp?.toDate?.() || new Date(0);
          return bTime - aTime; // Most recent first
        });
      
      setErrorLogs(logsData);
      setLoading(false);
    });
    
    return () => unsub();
  }, [open]);

  const loadErrorLogs = () => {
    // Force re-render by setting loading state
    setLoading(true);
    setTimeout(() => setLoading(false), 500);
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleToggleErrorRead = async (errorId, currentReadStatus) => {
    try {
      await updateDoc(doc(db, "ErrorLogs", errorId), {
        errorRead: !currentReadStatus,
      });
    } catch (error) {
      console.error("Error updating errorRead status:", error);
    }
  };

  const handleDeleteError = async (errorId) => {
    try {
      await deleteDoc(doc(db, "ErrorLogs", errorId));
    } catch (error) {
      console.error("Error deleting error log:", error);
    }
  };

  const handleClearAll = async () => {
    try {
      const batch = writeBatch(db);
      errorLogs.forEach((log) => batch.delete(doc(db, "ErrorLogs", log.id)));
      await batch.commit();
    } catch (error) {
      console.error("Error clearing all error logs:", error);
    }
  };

  const handleErrorClick = (error) => {
    setSelectedError(error);
    setErrorDetailOpen(true);
  };

  const handleErrorDetailClose = () => {
    setErrorDetailOpen(false);
    setSelectedError(null);
  };

  const addTestError = async () => {
    setAddingTestError(true);
    console.log("Adding test error...");
    try {
      const testErrors = [
        {
          message: "Test Error: Cannot read property 'map' of undefined",
          stack:
            "TypeError: Cannot read property 'map' of undefined\n    at Component.render (Calendar.js:245:23)\n    at finishClassComponent (react-dom.js:1952:14)\n    at updateClassComponent (react-dom.js:1927:24)",
          url: window.location.href,
          userAgent: navigator.userAgent,
        },
        {
          message: "Test Error: Network request failed",
          stack:
            "Error: Network request failed\n    at XMLHttpRequest.xhr.onerror (firebaseConfig.js:128:45)\n    at XMLHttpRequest.dispatch (firebase-app.js:234:12)",
          url: window.location.href,
          userAgent: navigator.userAgent,
        },
        {
          message: "Test Error: Maximum call stack size exceeded",
          stack:
            "RangeError: Maximum call stack size exceeded\n    at recursiveFunction (UserMenu.js:89:15)\n    at recursiveFunction (UserMenu.js:92:18)\n    at recursiveFunction (UserMenu.js:92:18)",
          url: window.location.href,
          userAgent: navigator.userAgent,
        },
      ];

      const randomError =
        testErrors[Math.floor(Math.random() * testErrors.length)];
      console.log("Adding test error with data:", randomError);

      const docRef = await addDoc(collection(db, "ErrorLogs"), {
        ...randomError,
        timestamp: serverTimestamp(),
        userId: null,
        errorRead: false,
        componentStack:
          "    in TestComponent\n    in ErrorBoundary\n    in App",
        isTestError: true, // Flag to identify test errors
      });

      console.log("Test error added successfully with ID:", docRef.id);
    } catch (error) {
      console.error("Error adding test error:", error);
    } finally {
      setAddingTestError(false);
    }
  };

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - errorLogs.length) : 0;

  const renderStatusChip = (errorRead) => {
    return (
      <Chip
        label={errorRead ? "Read" : "Unread"}
        color={errorRead ? "success" : "error"}
        size="small"
        sx={{
          fontWeight: 500,
          minWidth: 60,
          justifyContent: "center",
        }}
      />
    );
  };

  const renderErrorDetails = (log) => {
    let timestamp = null;
    if (log.timestamp?.toDate) {
      timestamp = log.timestamp.toDate();
    } else if (log.timestamp?.seconds) {
      timestamp = new Date(log.timestamp.seconds * 1000);
    } else if (log.timestamp) {
      timestamp = new Date(log.timestamp);
    }

    if (isMobile) {
      return (
        <Card
          key={log.id}
          variant="outlined"
          sx={{
            mb: 1,
            borderLeft: `4px solid ${
              log.errorRead
                ? theme.palette.success.main
                : theme.palette.error.main
            }`,
          }}
        >
          <CardHeader
            title={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  variant="subtitle2"
                  sx={{ 
                    wordBreak: "break-word",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" }
                  }}
                  onClick={() => handleErrorClick(log)}
                >
                  {log.message || "JavaScript Error"}
                </Typography>
                {renderStatusChip(log.errorRead)}
              </Box>
            }
            subheader={
              <Typography variant="caption" color="text.secondary">
                {timestamp ? format(timestamp, "PPpp") : "N/A"}
                {timestamp && (
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>
                    ({formatDistanceToNow(timestamp, { addSuffix: true })})
                  </span>
                )}
              </Typography>
            }
            sx={{ py: 1, px: 2 }}
          />
          <Divider />
          <CardContent sx={{ py: 1, px: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                URL: {log.url || "N/A"}
              </Typography>
            </Box>
            <Box mb={1}>
              <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                User: {(() => {
                  if (log.staffFirstName && log.staffLastName) {
                    return `${log.staffFirstName} ${log.staffLastName}${log.userEmail ? ` (${log.userEmail})` : ''}`;
                  }
                  if (log.userDisplayName) {
                    return `${log.userDisplayName}${log.userEmail ? ` (${log.userEmail})` : ''}`;
                  }
                  if (log.userEmail) {
                    return log.userEmail;
                  }
                  if (log.userId) {
                    return `UID: ${log.userId}`;
                  }
                  return "Unknown User";
                })()}
              </Typography>
            </Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={log.errorRead}
                      onChange={() =>
                        handleToggleErrorRead(log.id, log.errorRead)
                      }
                      size="small"
                    />
                  }
                  label="Read"
                />
                <IconButton
                  onClick={() => handleDeleteError(log.id)}
                  size="small"
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            {log.stack && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  fontFamily: "monospace",
                  bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  p: 1,
                  borderRadius: 1,
                  whiteSpace: "pre-wrap",
                  maxHeight: 100,
                  overflow: "auto",
                }}
              >
                {log.stack}
              </Typography>
            )}
          </CardContent>
        </Card>
      );
    }

    // Desktop view
    return (
      <TableRow hover key={log.id}>
        <TableCell>
          {timestamp ? (
            <Box>
              <div>{format(timestamp, "PPpp")}</div>
              <div style={{ fontSize: "0.75rem", opacity: 0.7 }}>
                {formatDistanceToNow(timestamp, { addSuffix: true })}
              </div>
            </Box>
          ) : (
            "N/A"
          )}
        </TableCell>
        <TableCell 
          sx={{ 
            maxWidth: 300, 
            wordBreak: "break-word",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" }
          }}
          onClick={() => handleErrorClick(log)}
        >
          {log.message || "JavaScript Error"}
        </TableCell>
        <TableCell sx={{ maxWidth: 200, wordBreak: "break-word" }}>
          {(() => {
            if (log.staffFirstName && log.staffLastName) {
              return `${log.staffFirstName} ${log.staffLastName}${log.userEmail ? ` (${log.userEmail})` : ''}`;
            }
            if (log.userDisplayName) {
              return `${log.userDisplayName}${log.userEmail ? ` (${log.userEmail})` : ''}`;
            }
            if (log.userEmail) {
              return log.userEmail;
            }
            if (log.userId) {
              return `UID: ${log.userId}`;
            }
            return "Unknown User";
          })()}
        </TableCell>
        <TableCell>{renderStatusChip(log.errorRead)}</TableCell>
        <TableCell sx={{ maxWidth: 200, wordBreak: "break-word" }}>
          {log.url || "N/A"}
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={log.errorRead}
                  onChange={() => handleToggleErrorRead(log.id, log.errorRead)}
                  size="small"
                />
              }
              label=""
            />
            <IconButton
              onClick={() => handleDeleteError(log.id)}
              size="small"
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        paper: {
          style: {
            height: isMobile ? "100%" : "80vh",
            maxHeight: isMobile ? "none" : "800px",
          },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Error Logs</Typography>
          <Box>
            <Tooltip title="Refresh">
              <IconButton
                onClick={loadErrorLogs}
                disabled={loading}
                size={isMobile ? "small" : "medium"}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size={isMobile ? "small" : "medium"}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers style={{ padding: 0, overflow: "hidden" }}>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="200px"
          >
            <CircularProgress />
          </Box>
        ) : errorLogs.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            height="200px"
            textAlign="center"
            p={2}
          >
            <div>DEBUG: errorLogs = {JSON.stringify(errorLogs)}</div>
            <Typography variant="subtitle1" color="textSecondary" gutterBottom>
              No errors found
            </Typography>
            <Typography variant="body2" color="textSecondary">
              No application errors have been recorded yet
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ p: 1, height: "100%", overflow: "auto" }}>
            {errorLogs
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((log) => renderErrorDetails(log))}
          </Box>
        ) : (
          <>
            <TableContainer
              style={{ maxHeight: "calc(100% - 53px)", overflow: "auto" }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Error Message</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {errorLogs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((log) => renderErrorDetails(log))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      {errorLogs.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={errorLogs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": {
              paddingLeft: 2,
              paddingRight: 2,
            },
          }}
        />
      )}

      <DialogActions
        sx={{ p: 2, pt: 1, display: "flex", justifyContent: "space-between" }}
      >
        <Box display="flex" gap={1}>
          <Button
            onClick={addTestError}
            color="secondary"
            variant="contained"
            size={isMobile ? "medium" : "small"}
            disabled={addingTestError}
            startIcon={addingTestError ? <CircularProgress size={16} /> : null}
          >
            {addingTestError ? "Adding..." : "Add Test Error"}
          </Button>
          <Button
            onClick={handleClearAll}
            color="error"
            variant="outlined"
            size={isMobile ? "medium" : "small"}
            disabled={errorLogs.length === 0}
          >
            Clear All
          </Button>
        </Box>
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          size={isMobile ? "medium" : "small"}
        >
          Close
        </Button>
      </DialogActions>

      {/* Error Detail Modal */}
      <Dialog
        open={errorDetailOpen}
        onClose={handleErrorDetailClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Error Details</Typography>
            <IconButton onClick={handleErrorDetailClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedError && (
            <Box>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Error Message:
              </Typography>
              <Typography variant="body2" paragraph sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', p: 2, borderRadius: 1 }}>
                {selectedError.message}
              </Typography>

              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                User Information:
              </Typography>
              <Typography variant="body2" paragraph sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', p: 2, borderRadius: 1 }}>
                {(() => {
                  const userInfo = [];
                  
                  if (selectedError.staffFirstName && selectedError.staffLastName) {
                    userInfo.push(`Name: ${selectedError.staffFirstName} ${selectedError.staffLastName}`);
                  } else if (selectedError.userDisplayName) {
                    userInfo.push(`Display Name: ${selectedError.userDisplayName}`);
                  }
                  
                  if (selectedError.userEmail) {
                    userInfo.push(`Email: ${selectedError.userEmail}`);
                  }
                  
                  if (selectedError.userId) {
                    userInfo.push(`User ID: ${selectedError.userId}`);
                  }
                  
                  if (selectedError.staffId) {
                    userInfo.push(`Staff ID: ${selectedError.staffId}`);
                  }
                  
                  return userInfo.length > 0 ? userInfo.join('\n') : 'Unknown User';
                })()}
              </Typography>

              {selectedError.stack && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Stack Trace:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    paragraph 
                    sx={{ 
                      fontFamily: 'monospace', 
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', 
                      p: 2, 
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.75rem'
                    }}
                  >
                    {selectedError.stack}
                  </Typography>
                </>
              )}

              {selectedError.componentStack && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Component Stack:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    paragraph 
                    sx={{ 
                      fontFamily: 'monospace', 
                      bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', 
                      p: 2, 
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.75rem'
                    }}
                  >
                    {selectedError.componentStack}
                  </Typography>
                </>
              )}

              {selectedError.userAgent && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    User Agent:
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', p: 2, borderRadius: 1 }}>
                    {selectedError.userAgent}
                  </Typography>
                </>
              )}

              {selectedError.url && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    URL:
                  </Typography>
                  <Typography variant="body2" paragraph sx={{ bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100', p: 2, borderRadius: 1 }}>
                    {selectedError.url}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleErrorDetailClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ErrorLogsDialog;
